/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useRef } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// DefaultLineChart configurations
import configs from "examples/Charts/LineCharts/DefaultLineChart/configs";

// Material Dashboard 2 React base styles
import colors from "assets/theme/base/colors";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function DefaultLineChart({
  icon,
  title,
  description,
  height,
  chart,
  interactiveTooltip,
  onTooltipInfoClick,
  onTooltipTrendClick,
  showTooltipTrendButton,
}) {
  const chartWrapRef = useRef(null);

  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        tension: 0,
        pointRadius: 3,
        borderWidth: 4,
        backgroundColor: "transparent",
        fill: true,
        pointBackgroundColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        borderColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        maxBarThickness: 6,
      }))
    : [];

  const { data, options } = configs(chart.labels || [], chartDatasets);

  const chartOptions = useMemo(() => {
    if (!interactiveTooltip) {
      return options;
    }

    return {
      ...options,

      // Selecionar o mês apenas por clique (desktop)
      // ou toque (mobile), nunca por movimento do rato.
      events: ["click"],

      interaction: {
        ...(options.interaction || {}),
        mode: "index",
        intersect: false,
      },

      plugins: {
        ...(options.plugins || {}),

        tooltip: {
          enabled: false,

          external: ({ chart: chartInstance, tooltip }) => {
            const parent = chartInstance?.canvas?.parentNode;
            if (!parent) return;

            let tooltipEl = parent.querySelector(".jf-chart-external-tooltip");

            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.className = "jf-chart-external-tooltip";

              Object.assign(tooltipEl.style, {
                position: "absolute",
                zIndex: "20",
                minWidth: "210px",
                padding: "9px 11px",
                borderRadius: "7px",
                background: "rgba(0, 0, 0, 0.82)",
                color: "#fff",
                fontSize: "12px",
                lineHeight: "1.35",
                pointerEvents: "auto",
                opacity: "0",
                transition: "opacity 0.12s ease",
                boxShadow: "0 4px 14px rgba(0,0,0,0.22)",
              });

              tooltipEl.dataset.hovering = "0";

              tooltipEl.addEventListener("mouseenter", () => {
                tooltipEl.dataset.hovering = "1";

                if (tooltipEl._hideTimer) {
                  clearTimeout(tooltipEl._hideTimer);
                  tooltipEl._hideTimer = null;
                }
              });

              tooltipEl.addEventListener("mouseleave", () => {
                tooltipEl.dataset.hovering = "0";

                tooltipEl._hideTimer = setTimeout(() => {
                  tooltipEl.style.opacity = "0";
                }, 150);
              });

              parent.appendChild(tooltipEl);
            }

            if (tooltip.opacity === 0) {
              if (tooltipEl.dataset.hovering !== "1") {
                if (tooltipEl._hideTimer) {
                  clearTimeout(tooltipEl._hideTimer);
                }

                tooltipEl._hideTimer = setTimeout(() => {
                  if (tooltipEl.dataset.hovering !== "1") {
                    tooltipEl.style.opacity = "0";
                  }
                }, 220);
              }

              return;
            }

            if (tooltipEl._hideTimer) {
              clearTimeout(tooltipEl._hideTimer);
              tooltipEl._hideTimer = null;
            }

            tooltipEl.innerHTML = "";

            const titleText = tooltip.title?.[0] || "";

            const titleEl = document.createElement("div");
            titleEl.textContent = titleText;

            Object.assign(titleEl.style, {
              fontWeight: "700",
              marginBottom: "6px",
            });

            tooltipEl.appendChild(titleEl);

            (tooltip.dataPoints || []).forEach((point) => {
              const row = document.createElement("div");

              Object.assign(row.style, {
                display: "flex",
                alignItems: "center",
                gap: "9px",
                marginTop: "7px",
                minHeight: "26px",
                whiteSpace: "nowrap",
              });

              const bullet = document.createElement("span");

              Object.assign(bullet.style, {
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                flex: "0 0 auto",
                background:
                  point.dataset?.borderColor || point.dataset?.pointBackgroundColor || "#fff",
              });

              row.appendChild(bullet);

              const text = document.createElement("span");
              text.style.flex = "1";

              const label = point.dataset?.label || "";
              const valor = point.formattedValue ?? point.raw ?? "";

              text.textContent = `${label}: ${valor}`;
              row.appendChild(text);

              const infoButton = document.createElement("button");
              infoButton.type = "button";
              infoButton.title = "Ver detalhe";
              infoButton.setAttribute("aria-label", `Ver detalhe de ${label}`);

              Object.assign(infoButton.style, {
                width: "24px",
                height: "24px",
                padding: "0",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.85)",
                background: "transparent",
                color: "#fff",
                fontSize: "12px",
                fontWeight: "700",
                lineHeight: "22px",
                textAlign: "center",
                cursor: "pointer",
                flex: "0 0 auto",
              });

              infoButton.textContent = "i";

              infoButton.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();

                if (typeof onTooltipInfoClick !== "function") return;

                onTooltipInfoClick({
                  datasetIndex: point.datasetIndex,
                  dataIndex: point.dataIndex,
                  datasetLabel: point.dataset?.label || "",
                  label: titleText,
                  value: point.raw,
                });
              });

              const mostrarTrend =
                typeof onTooltipTrendClick === "function" &&
                (typeof showTooltipTrendButton !== "function" ||
                  showTooltipTrendButton({
                    datasetIndex: point.datasetIndex,
                    dataIndex: point.dataIndex,
                    datasetLabel: point.dataset?.label || "",
                    label: titleText,
                    value: point.raw,
                  }));

              if (mostrarTrend) {
                const trendButton = document.createElement("button");
                trendButton.type = "button";
                trendButton.title = "Comparar evolução até esta data";
                trendButton.setAttribute(
                  "aria-label",
                  `Comparar evolução de ${point.dataset?.label || ""}`
                );

                Object.assign(trendButton.style, {
                  width: "24px",
                  height: "24px",
                  padding: "0",
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.85)",
                  background: "transparent",
                  color: "#fff",
                  cursor: "pointer",
                  flex: "0 0 auto",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                });

                const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

                svg.setAttribute("viewBox", "0 0 24 24");
                svg.setAttribute("width", "15");
                svg.setAttribute("height", "15");
                svg.setAttribute("aria-hidden", "true");

                const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

                path.setAttribute(
                  "d",
                  "M3.5 18.49 9.5 12.48l4 4L22 6.92 20.59 5.5 13.5 13.48l-4-4L2 17z"
                );
                path.setAttribute("fill", "currentColor");

                svg.appendChild(path);
                trendButton.appendChild(svg);

                trendButton.addEventListener("click", (event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  onTooltipTrendClick({
                    datasetIndex: point.datasetIndex,
                    dataIndex: point.dataIndex,
                    datasetLabel: point.dataset?.label || "",
                    label: titleText,
                    value: point.raw,
                  });
                });

                row.appendChild(trendButton);
              }

              row.appendChild(infoButton);
              tooltipEl.appendChild(row);
            });

            const canvas = chartInstance.canvas;

            // Janela centrada horizontalmente no gráfico,
            // independentemente do mês selecionado.
            const larguraDisponivel = parent.clientWidth;
            const larguraTooltip = tooltipEl.offsetWidth;

            tooltipEl.style.opacity = "1";
            tooltipEl.style.left = `${Math.max(8, (larguraDisponivel - larguraTooltip) / 2)}px`;

            // Posição vertical fixa na parte superior do gráfico.
            tooltipEl.style.top = `${canvas.offsetTop + 8}px`;

            // Evitar ultrapassar a largura do gráfico em mobile.
            tooltipEl.style.maxWidth = `${Math.max(0, larguraDisponivel - 16)}px`;
            tooltipEl.style.boxSizing = "border-box";
          },
        },
      },
    };
  }, [
    options,
    interactiveTooltip,
    onTooltipInfoClick,
    onTooltipTrendClick,
    showTooltipTrendButton,
  ]);

  const renderChart = (
    <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "dark"}
              variant="gradient"
              coloredShadow={icon.color || "dark"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      {useMemo(
        () => (
          <MDBox
            ref={chartWrapRef}
            height={height}
            sx={{
              position: "relative",
            }}
          >
            <Line data={data} options={chartOptions} redraw />
          </MDBox>
        ),
        [chart, height, chartOptions]
      )}
    </MDBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of DefaultLineChart
DefaultLineChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
  interactiveTooltip: false,
  onTooltipInfoClick: null,
  onTooltipTrendClick: null,
  showTooltipTrendButton: null,
};

// Typechecking props for the DefaultLineChart
DefaultLineChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  interactiveTooltip: PropTypes.bool,
  onTooltipInfoClick: PropTypes.func,
  onTooltipTrendClick: PropTypes.func,
  showTooltipTrendButton: PropTypes.func,
};

export default DefaultLineChart;
